import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { companiesApi } from '../services/api/companies';
import { ApiRequestError } from '../services/api/client';
import type { CargoQuoteRequest } from '../types/quotes';
import { quoteKeys } from './quoteKeys';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ContactedCompany {
  id: number;
  company_name: string;
  company_email: string;
  status: string;
  contacted_at: string;
  notes?: string;
}

export interface AdminQuoteResponseItem {
  id: number;
  transportation_company_id: number;
  company_name: string;
  price: string;
  currency: string;
  estimated_delivery_date: string;
  observations?: string;
  valid_until?: string;
  loading_date?: string;
  no_specific_loading_date?: boolean;
  created_by: string;
  created_at: string;
  contact_requested_at?: string;
  contacted_at?: string | null;
}

export interface ResponseFormState {
  transportation_company_id: string;
  price: string;
  currency: string;
  estimated_delivery_date: string;
  loading_date: string;
  no_specific_loading_date: boolean;
  valid_until: string;
  observations: string;
}

export interface ResponseFieldErrors {
  transportation_company_id: string[];
  price: string[];
  currency: string[];
  estimated_delivery_date: string[];
  loading_date: string[];
}

export interface ResponseValidationResult {
  fieldErrors: ResponseFieldErrors;
  formErrors: string[];
  isValid: boolean;
}

export const createEmptyResponseFieldErrors = (): ResponseFieldErrors => ({
  transportation_company_id: [],
  price: [],
  currency: [],
  estimated_delivery_date: [],
  loading_date: [],
});

export const SUPPORTED_CURRENCIES = [
  'EUR',
  'GBP',
  'USD',
  'BGN',
  'CHF',
  'CZK',
  'DKK',
  'HUF',
  'NOK',
  'PLN',
  'RON',
  'SEK',
] as const;
export const DEFAULT_CURRENCY = 'EUR';

const RESPONSE_VALIDATION_ERROR = 'admin.quote.validation.fix_errors';

export interface UseAdminQuoteDetailReturn {
  id: string | undefined;

  // Data
  quote: CargoQuoteRequest | null;
  contactedCompanies: ContactedCompany[];
  loading: boolean;

  // Modal state
  isResponseModalOpen: boolean;
  setIsResponseModalOpen: (open: boolean) => void;

  // Response form state
  transportationCompanies: any[];
  responseForm: ResponseFormState;
  setResponseForm: React.Dispatch<React.SetStateAction<ResponseFormState>>;
  fieldErrors: ResponseFieldErrors;
  setFieldErrors: React.Dispatch<React.SetStateAction<ResponseFieldErrors>>;
  submittingResponse: boolean;
  editingResponseId: number | null;
  setEditingResponseId: (id: number | null) => void;
  formErrors: string[];
  setFormErrors: (errors: string[]) => void;
  markingContactedId: number | null;

  // Handlers
  handleEditResponse: (response: AdminQuoteResponseItem) => void;
  handleDeleteResponse: (responseId: number) => Promise<void>;
  handleMarkContacted: (responseId: number) => Promise<void>;
  handleRejectQuote: () => Promise<void>;
  handleSubmitResponse: () => Promise<boolean>;
  handleDownloadAllImages: () => Promise<void>;
  handleDownloadImage: (imageId: number) => Promise<void>;

  validate: () => ResponseValidationResult;

  // Pure helpers (returned for convenience)
  formatBytes: (bytes: number) => string;
  getStatusBadgeVariant: (status: string) => 'default' | 'destructive' | 'outline' | 'secondary';
  getStatusBadgeClass: (status: string) => string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export const formatBytes = (bytes: number): string => {
  if (!bytes && bytes !== 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  const rounded = i === 0 ? String(Math.round(value)) : value.toFixed(1);
  return `${rounded} ${units[i]}`;
};

export const getStatusBadgeVariant = (status: string): 'default' | 'destructive' | 'outline' | 'secondary' => {
  switch (status) {
    case 'pending': return 'secondary';
    case 'quoted': return 'default';
    case 'awaiting_contact': return 'outline';
    case 'contacted': return 'default';
    case 'rejected': return 'destructive';
    default: return 'outline';
  }
};

export const getStatusBadgeClass = (status: string): string => {
  switch (status) {
    case 'awaiting_contact': return 'bg-sky-50 text-sky-700 border-sky-200';
    case 'contacted': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    default: return '';
  }
};

// Accepted syntax for a newly submitted price (#307). Price is stored as a
// string in major units so significant digits/formatting survive (#277), and
// currency is an explicit, separate field. The submitted text must therefore be
// a positive decimal written with digits and an optional dot-decimal fraction —
// no sign, thousands separator, currency symbol, scientific notation or
// trailing text. Mirrors the server-side contract on QuoteResponse so the same
// value is accepted or rejected on both sides; it is never reduced to a numeric
// substring.
export const PRICE_PATTERN = /^\d+(\.\d+)?$/;

export const hasValidPriceSyntax = (value: string): boolean =>
  PRICE_PATTERN.test(value.trim());

// ---------------------------------------------------------------------------
// Query key helpers
// ---------------------------------------------------------------------------

// Admin quote keys are defined in the shared quote key contract (issue #193).
// The transportation-companies key is a separate admin resource and is not a
// quote key, so it stays inline here.
const ADMIN_TRANSPORTATION_COMPANIES_KEY = ['admin', 'transportation-companies'] as const;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useAdminQuoteDetail = (id: string | undefined): UseAdminQuoteDetailReturn => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false);

  const [transportationCompanies, setTransportationCompanies] = useState<any[]>([]);
  const [responseForm, setResponseForm] = useState<ResponseFormState>({
    transportation_company_id: '',
    price: '',
    currency: DEFAULT_CURRENCY,
    estimated_delivery_date: '',
    loading_date: '',
    no_specific_loading_date: false,
    valid_until: '',
    observations: '',
  });
  const [fieldErrors, setFieldErrors] = useState<ResponseFieldErrors>(createEmptyResponseFieldErrors());
  const [editingResponseId, setEditingResponseId] = useState<number | null>(null);
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [markingContactedId, setMarkingContactedId] = useState<number | null>(null);

  // ---- TanStack Queries for server state ----

  const quoteQuery = useQuery({
    queryKey: quoteKeys.admin.detail(id),
    queryFn: () => companiesApi.getAdminQuote(id!),
    enabled: !!id,
  });

  const contactedQuery = useQuery({
    queryKey: quoteKeys.admin.contactedCompanies(id),
    queryFn: () => companiesApi.getAdminQuoteContactedCompanies(id!),
    enabled: !!id,
  });

  const transportationQuery = useQuery({
    queryKey: ADMIN_TRANSPORTATION_COMPANIES_KEY,
    queryFn: () => companiesApi.getAdminTransportationCompanies(),
    enabled: isResponseModalOpen,
  });

  // ---- Derived data from queries ----

  const quote = quoteQuery.data?.cargo_quote_request ?? null;
  const contactedCompanies = contactedQuery.data?.company_contacts ?? [];
  const loading = quoteQuery.isPending || contactedQuery.isPending;

  // ---- Effects ------------------------------------------------------------

  // Sync transportation companies from TanStack Query into local state
  useEffect(() => {
    if (transportationQuery.data && transportationCompanies.length === 0) {
      setTransportationCompanies(transportationQuery.data.transportation_companies);
    }
  }, [transportationQuery.data, transportationCompanies.length]);

  // Auto-open response modal when URL has ?respond=true
  useEffect(() => {
    if (!loading && quote && searchParams.get('respond') === 'true') {
      setIsResponseModalOpen(true);
      searchParams.delete('respond');
      setSearchParams(searchParams, { replace: true });
    }
  }, [loading, quote, searchParams, setSearchParams]);

  // ---- Mutations (TanStack Query) -------------------------------------------

  const invalidateQuoteData = useCallback(() => {
    if (!id) return;
    queryClient.invalidateQueries({ queryKey: quoteKeys.admin.lists() });
    queryClient.invalidateQueries({ queryKey: quoteKeys.admin.detail(id) });
    queryClient.invalidateQueries({ queryKey: quoteKeys.admin.contactedCompanies(id) });
  }, [id, queryClient]);

  const validate = useCallback((): ResponseValidationResult => {
    const nextFieldErrors = createEmptyResponseFieldErrors();
    const nextFormErrors: string[] = [];

    const addFormError = () => {
      if (nextFormErrors.length === 0) {
        nextFormErrors.push(RESPONSE_VALIDATION_ERROR);
      }
    };

    if (!responseForm.transportation_company_id.trim()) {
      nextFieldErrors.transportation_company_id.push('admin.quote.validation.transportation_company_required');
      addFormError();
    }

    const priceValue = responseForm.price.trim();
    if (!priceValue) {
      nextFieldErrors.price.push('admin.quote.validation.price_required');
      addFormError();
    } else if (!hasValidPriceSyntax(priceValue)) {
      nextFieldErrors.price.push('admin.quote.validation.price_invalid');
      addFormError();
    } else if (!(Number(priceValue) > 0)) {
      nextFieldErrors.price.push('admin.quote.validation.price_positive');
      addFormError();
    }

    if (!SUPPORTED_CURRENCIES.includes(responseForm.currency as (typeof SUPPORTED_CURRENCIES)[number])) {
      nextFieldErrors.currency.push('admin.quote.validation.currency_required');
      addFormError();
    }

    // Schedule dates are optional only when "no specific date" is set.
    if (!responseForm.no_specific_loading_date) {
      if (!responseForm.estimated_delivery_date.trim()) {
        nextFieldErrors.estimated_delivery_date.push('admin.quote.validation.estimated_delivery_date_required');
        addFormError();
      }
      if (!responseForm.loading_date.trim()) {
        nextFieldErrors.loading_date.push('admin.quote.validation.loading_date_required');
        addFormError();
      }
    }

    setFieldErrors(nextFieldErrors);
    setFormErrors(nextFormErrors);

    return {
      fieldErrors: nextFieldErrors,
      formErrors: nextFormErrors,
      isValid: nextFormErrors.length === 0,
    };
  }, [responseForm]);

  const deleteResponseMutation = useMutation({
    mutationFn: (responseId: number) => companiesApi.deleteQuoteResponse(id!, responseId),
    onSuccess: () => {
      invalidateQuoteData();
      toast.success(t('admin.quote.response_deleted'));
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : t('admin.quote.delete_response_failed'));
    },
  });

  const markContactedMutation = useMutation({
    mutationFn: (responseId: number) => companiesApi.markQuoteResponseContacted(id!, responseId),
    onMutate: (responseId) => {
      setMarkingContactedId(responseId);
    },
    onSuccess: () => {
      invalidateQuoteData();
      toast.success(t('admin.quote.mark_contacted_success'));
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : t('admin.quote.mark_contacted_failed'));
    },
    onSettled: () => {
      setMarkingContactedId(null);
    },
  });

  const rejectQuoteMutation = useMutation({
    mutationFn: () => companiesApi.updateAdminQuote(id!, { status: 'rejected' }),
    onSuccess: () => {
      invalidateQuoteData();
      toast.success(t('admin.quote.rejected'));
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : t('admin.quote.reject_failed'));
    },
  });

  const submitResponseMutation = useMutation({
    mutationFn: async () => {
      if (editingResponseId) {
        await companiesApi.updateQuoteResponse(id!, editingResponseId, responseForm);
      } else {
        await companiesApi.createQuoteResponse(id!, responseForm);
      }
    },
    onSuccess: () => {
      invalidateQuoteData();
      setFieldErrors(createEmptyResponseFieldErrors());
      setFormErrors([]);
      setIsResponseModalOpen(false);
      setEditingResponseId(null);
      setResponseForm({
        transportation_company_id: '',
        price: '',
        currency: DEFAULT_CURRENCY,
        estimated_delivery_date: '',
        loading_date: '',
        no_specific_loading_date: false,
        valid_until: '',
        observations: '',
      });
      toast.success(editingResponseId
        ? t('admin.quote.response_updated')
        : t('admin.quote.response_created'));
    },
    onError: (err) => {
      if (err instanceof ApiRequestError && err.errors) {
        setFieldErrors(createEmptyResponseFieldErrors());
        setFormErrors(err.errors);
      } else {
        toast.error(err instanceof Error ? err.message : t('admin.quote.save_response_failed'));
      }
    },
  });

  const submittingResponse = submitResponseMutation.isPending;

  // ---- Handlers -----------------------------------------------------------

  const handleEditResponse = useCallback((response: AdminQuoteResponseItem) => {
    setEditingResponseId(response.id);
    setFieldErrors(createEmptyResponseFieldErrors());
    setFormErrors([]);
    setResponseForm({
      transportation_company_id: String(response.transportation_company_id),
      price: response.price,
      currency: response.currency || DEFAULT_CURRENCY,
      estimated_delivery_date: response.estimated_delivery_date || '',
      loading_date: response.loading_date || '',
      no_specific_loading_date: response.no_specific_loading_date || false,
      valid_until: response.valid_until || '',
      observations: response.observations || '',
    });
    setIsResponseModalOpen(true);
  }, []);

  const handleDeleteResponse = useCallback(async (responseId: number) => {
    if (!id) return;
    await deleteResponseMutation.mutateAsync(responseId);
  }, [id, deleteResponseMutation]);

  const handleMarkContacted = useCallback(async (responseId: number) => {
    if (!id) return;
    await markContactedMutation.mutateAsync(responseId);
  }, [id, markContactedMutation]);

  const handleRejectQuote = useCallback(async () => {
    if (!id) return;
    await rejectQuoteMutation.mutateAsync();
  }, [id, rejectQuoteMutation]);

  const handleSubmitResponse = useCallback(async () => {
    if (!id) return false;
    try {
      const validation = validate();
      if (!validation.isValid) {
        return false;
      }
      await submitResponseMutation.mutateAsync();
      return true;
    } catch {
      return false;
    }
  }, [id, submitResponseMutation, validate]);

  const handleDownloadAllImages = useCallback(async () => {
    if (!quote) return;
    try {
      await companiesApi.downloadAllAdminQuoteAttachments(quote.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('admin.quote.download_all_files_failed'));
    }
  }, [quote, t]);

  const handleDownloadImage = useCallback(async (imageId: number) => {
    if (!quote) return;
    try {
      await companiesApi.downloadAdminQuoteAttachment(quote.id, imageId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('admin.quote.download_file_failed'));
    }
  }, [quote, t]);

  // ---- Return -------------------------------------------------------------

  return {
    id,
    quote,
    contactedCompanies,
    loading,
    isResponseModalOpen,
    setIsResponseModalOpen,
    transportationCompanies,
    responseForm,
    setResponseForm,
    fieldErrors,
    setFieldErrors,
    submittingResponse,
    editingResponseId,
    setEditingResponseId,
    formErrors,
    setFormErrors,
    markingContactedId,
    handleEditResponse,
    handleDeleteResponse,
    handleMarkContacted,
    handleRejectQuote,
    handleSubmitResponse,
    handleDownloadAllImages,
    handleDownloadImage,
    validate,
    formatBytes,
    getStatusBadgeVariant,
    getStatusBadgeClass,
  };
};
